// Keep Fit App - 食物分析頁面

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import ImageUploader from '../components/ImageUploader';
import { analyzeFood, createRecord } from '../services/api';
import type { FoodAnalysis } from '../types';

// 可用模型列表
const AI_MODELS = [
  { id: 'auto', name: '自動選擇', description: '系統自動選擇可用模型' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: '最新快速模型' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: '最強大模型' },
  { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', description: '穩定版本' },
  { id: 'gemini-2.0-flash-lite', name: 'Gemini 2.0 Flash Lite', description: '輕量版，節省配額' },
];

export default function Analyze() {
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('');
  const [analysis, setAnalysis] = useState<FoodAnalysis | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('auto');
  const [selectedMeal, setSelectedMeal] = useState<string>('lunch');
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState<'upload' | 'analyzing' | 'result' | 'saving'>('upload');

  // 分析突變
  const analyzeMutation = useMutation({
    mutationFn: () => analyzeFood(imageBase64!, mimeType, selectedModel === 'auto' ? undefined : selectedModel),
    onSuccess: (response) => {
      if (response.success && response.data) {
        setAnalysis(response.data.analysis);
        setStep('result');
      } else {
        alert(`分析失敗: ${response.error?.message || '未知錯誤'}`);
        setStep('upload');
      }
    },
    onError: (error) => {
      alert(`分析失敗: ${error.message}`);
      setStep('upload');
    },
  });

  // 保存記錄突變
  const saveMutation = useMutation({
    mutationFn: () => createRecord({
      mealType: selectedMeal,
      analysis: analysis!,
      notes,
    }),
    onSuccess: (response) => {
      if (response.success) {
        alert('✅ 已成功保存記錄！');
        resetForm();
      } else {
        alert(`保存失敗: ${response.error?.message}`);
        setStep('result');
      }
    },
    onError: (error) => {
      alert(`保存失敗: ${error.message}`);
      setStep('result');
    },
  });

  const handleImageSelect = (base64: string, type: string) => {
    setImageBase64(base64);
    setMimeType(type);
  };

  const handleAnalyze = () => {
    if (!imageBase64) return;
    setStep('analyzing');
    analyzeMutation.mutate();
  };

  const handleSave = () => {
    if (!analysis) return;
    setStep('saving');
    saveMutation.mutate();
  };

  const resetForm = () => {
    setImageBase64(null);
    setMimeType('');
    setAnalysis(null);
    setSelectedMeal('lunch');
    setNotes('');
    setStep('upload');
  };

  const mealTypes = [
    { value: 'breakfast', label: '早餐', icon: '🌅' },
    { value: 'lunch', label: '午餐', icon: '☀️' },
    { value: 'dinner', label: '晚餐', icon: '🌙' },
    { value: 'snack', label: '小食', icon: '🍪' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-gray-800">📸 食物分析</h1>

      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="space-y-4">
          {/* AI 模型選擇 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-medium mb-3">🤖 AI 模型</h3>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full border rounded-lg p-2"
            >
              {AI_MODELS.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} - {model.description}
                </option>
              ))}
            </select>
            {selectedModel !== 'auto' && (
              <p className="text-xs text-gray-500 mt-1">
                ⚠️ 如所選模型不可用，系統會自動切換
              </p>
            )}
          </div>

          <ImageUploader 
            onImageSelect={handleImageSelect}
            disabled={analyzeMutation.isPending}
          />
          
          <button
            onClick={handleAnalyze}
            disabled={!imageBase64 || analyzeMutation.isPending}
            className="w-full bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            🔍 開始分析
          </button>
        </div>
      )}

      {/* Step: Analyzing */}
      {step === 'analyzing' && (
        <div className="text-center py-12">
          <div className="text-6xl animate-bounce mb-4">🔮</div>
          <p className="text-gray-600">AI 正在分析你嘅食物...</p>
          <p className="text-sm text-gray-400 mt-2">
            使用模型: {selectedModel === 'auto' ? '自動選擇' : AI_MODELS.find(m => m.id === selectedModel)?.name}
          </p>
        </div>
      )}

      {/* Step: Result */}
      {step === 'result' && analysis && (
        <div className="space-y-4">
          {/* 分析結果 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">{analysis.foodName}</h2>
              <div className="flex gap-2">
                {analysis.usedModel && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {analysis.usedModel}
                  </span>
                )}
                <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">
                  {Math.round(analysis.confidence * 100)}% 信心
                </span>
              </div>
            </div>

            {/* 營養資訊 */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-orange-600">
                  {analysis.nutrition.calories}
                </div>
                <div className="text-xs text-gray-500">卡路里</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-red-600">
                  {analysis.nutrition.protein}g
                </div>
                <div className="text-xs text-gray-500">蛋白質</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-yellow-600">
                  {analysis.nutrition.fat}g
                </div>
                <div className="text-xs text-gray-500">脂肪</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-blue-600">
                  {analysis.nutrition.carbs}g
                </div>
                <div className="text-xs text-gray-500">碳水</div>
              </div>
              <div className="bg-pink-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-pink-600">
                  {analysis.nutrition.sugar}g
                </div>
                <div className="text-xs text-gray-500">糖</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-purple-600">
                  {analysis.nutrition.sodium}mg
                </div>
                <div className="text-xs text-gray-500">鈉</div>
              </div>
            </div>

            {/* 食材 */}
            {analysis.ingredients && analysis.ingredients.length > 0 && (
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 mb-1">食材</h3>
                <div className="flex flex-wrap gap-1">
                  {analysis.ingredients.map((ing, i) => (
                    <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {ing}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 健康提示 */}
            {analysis.healthTips && (
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                💡 {analysis.healthTips}
              </div>
            )}
          </div>

          {/* 選擇餐類 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-medium mb-3">選擇餐類</h3>
            <div className="grid grid-cols-4 gap-2">
              {mealTypes.map((meal) => (
                <button
                  key={meal.value}
                  onClick={() => setSelectedMeal(meal.value)}
                  className={`p-2 rounded-lg text-center ${
                    selectedMeal === meal.value
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <div className="text-xl">{meal.icon}</div>
                  <div className="text-xs mt-1">{meal.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* 備註 */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-medium mb-2">備註（選填）</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="例如：少甜、加辣..."
              className="w-full border rounded-lg p-2 text-sm"
              rows={2}
            />
          </div>

          {/* 操作按鈕 */}
          <div className="flex gap-3">
            <button
              onClick={resetForm}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-300"
            >
              重新分析
            </button>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="flex-1 bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 disabled:bg-gray-300"
            >
              {saveMutation.isPending ? '保存中...' : '💾 保存記錄'}
            </button>
          </div>
        </div>
      )}

      {/* Step: Saving */}
      {step === 'saving' && (
        <div className="text-center py-12">
          <div className="text-6xl animate-spin mb-4">💾</div>
          <p className="text-gray-600">正在保存記錄...</p>
        </div>
      )}
    </div>
  );
}
